import {
  Alert,
  Badge,
  Card,
  Grid,
  Group,
  Loader,
  Text,
  Title,
} from "@mantine/core";
import { IconFolderOff } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";

import { api, type ProjectSummary } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

export function HomePage() {
  const { isAdmin } = useAuth();
  const { data, isLoading, error } = useQuery<ProjectSummary[]>({
    queryKey: ["projects"],
    queryFn: () => api.get<ProjectSummary[]>("/api/projects"),
  });

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <Alert color="red" title="Could not load projects">
        {error instanceof Error ? error.message : "Unexpected error."}
      </Alert>
    );
  }

  const projects = data ?? [];

  // A list of one is pure friction — a click that only ever goes one place.
  // `replace` so the list is never a back-button stop between login and the
  // project. Applies equally to an admin whose whole instance has one
  // project and a viewer granted exactly one.
  if (projects.length === 1) {
    return <Navigate to={`/projects/${projects[0].slug}`} replace />;
  }

  return (
    <>
      <Title order={2} mb="xs">
        Projects
      </Title>
      <Text c="dimmed" mb="lg">
        {isAdmin
          ? "You have access to all projects."
          : "Projects you have been granted access to."}
      </Text>

      {projects.length === 0 ? (
        <Card withBorder padding="xl">
          <Group>
            <IconFolderOff size={20} />
            <div>
              <Text fw={500}>No projects yet</Text>
              <Text size="sm" c="dimmed">
                {isAdmin
                  ? "Projects appear here once a project folder exists in the data directory."
                  : "You have not been granted access to any projects. Contact an administrator."}
              </Text>
            </div>
          </Group>
        </Card>
      ) : (
        <Grid>
          {projects.map((project) => (
            <Grid.Col key={project.slug} span={{ base: 12, sm: 6, lg: 4 }}>
              <Card
                component={Link}
                to={`/projects/${project.slug}`}
                withBorder
                padding="lg"
                h="100%"
                style={{
                  textDecoration: "none",
                  ...(project.color
                    ? { backgroundColor: `var(--mantine-color-${project.color}-light)` }
                    : undefined),
                }}
              >
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Text fw={600}>{project.short_title ?? project.title}</Text>
                  {project.has_error && (
                    <Badge color="orange" variant="light" size="sm">
                      Needs attention
                    </Badge>
                  )}
                </Group>
                {project.description && (
                  <Text size="sm" c="dimmed" mt="xs">
                    {project.description}
                  </Text>
                )}
                {/* Detail is admin-only; a viewer sees the badge alone. */}
                {project.config_error && (
                  <Text size="xs" c="orange" mt="xs">
                    {project.config_error}
                  </Text>
                )}
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      )}
    </>
  );
}
